// Pattern Management Modal component
// Allows editing, viewing stats, and managing patterns

import { patternsApi, type Pattern, type PatternType, type PatternStatus } from '../services/patterns-api';

export class PatternModal {
  private pattern: Pattern | null = null;
  private isOpen = false;
  private hasChanges = false;
  private onSaveCallback: ((pattern: Pattern) => void) | null = null;
  private onDeleteCallback: ((patternId: string, patternType: PatternType) => void) | null = null;

  // DOM elements
  private overlayEl: HTMLElement;
  private titleInput: HTMLInputElement;
  private typeEl: HTMLElement;
  private statusIndicator: HTMLElement;
  private statusSelect: HTMLSelectElement;
  private descriptionEl: HTMLTextAreaElement;
  private charCountEl: HTMLElement;
  private examplesContainer: HTMLElement;
  private statVideos: HTMLElement;
  private statPerf: HTMLElement;
  private statWeek: HTMLElement;
  private statTrend: HTMLElement;
  private mergeList: HTMLElement;

  // Type colors
  private typeColors: Record<PatternType, string> = {
    hook: '#49e619',
    script: '#60a5fa',
    visual: '#a78bfa',
    cta: '#fbbf24',
    concept: '#f472b6'
  };

  constructor() {
    this.overlayEl = document.getElementById('pattern-modal-overlay')!;
    this.titleInput = document.getElementById('pattern-modal-title') as HTMLInputElement;
    this.typeEl = document.getElementById('pattern-modal-type')!;
    this.statusIndicator = document.getElementById('pattern-modal-status-indicator')!;
    this.statusSelect = document.getElementById('pattern-modal-status') as HTMLSelectElement;
    this.descriptionEl = document.getElementById('pattern-modal-description') as HTMLTextAreaElement;
    this.charCountEl = document.getElementById('pattern-modal-char-count')!;
    this.examplesContainer = document.getElementById('pattern-modal-examples')!;
    this.statVideos = document.getElementById('pattern-modal-stat-videos')!;
    this.statPerf = document.getElementById('pattern-modal-stat-perf')!;
    this.statWeek = document.getElementById('pattern-modal-stat-week')!;
    this.statTrend = document.getElementById('pattern-modal-stat-trend')!;
    this.mergeList = document.getElementById('pattern-modal-merge-list')!;

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Close button
    document.getElementById('pattern-modal-close')?.addEventListener('click', () => {
      this.close();
    });

    // Cancel button
    document.getElementById('pattern-modal-cancel')?.addEventListener('click', () => {
      this.close();
    });

    // Save button
    document.getElementById('pattern-modal-save')?.addEventListener('click', () => {
      this.save();
    });

    // Delete button
    document.getElementById('pattern-modal-delete')?.addEventListener('click', () => {
      this.confirmDelete();
    });

    // Overlay click to close
    this.overlayEl.addEventListener('click', (e) => {
      if (e.target === this.overlayEl) {
        this.close();
      }
    });

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Title input change
    this.titleInput.addEventListener('input', () => {
      this.hasChanges = true;
    });

    // Description change + character count
    this.descriptionEl.addEventListener('input', () => {
      this.hasChanges = true;
      this.updateCharCount();
    });

    // Status change
    this.statusSelect.addEventListener('change', () => {
      this.hasChanges = true;
      this.updateStatusIndicator();
    });

    // Add example button
    document.getElementById('pattern-modal-add-example')?.addEventListener('click', () => {
      this.addExample();
    });

    // Manage examples button
    document.getElementById('pattern-modal-manage-examples')?.addEventListener('click', () => {
      this.manageExamples();
    });
  }

  /**
   * Open modal with pattern data
   */
  async open(pattern: Pattern): Promise<void> {
    this.pattern = pattern;
    this.hasChanges = false;

    // Populate fields
    this.titleInput.value = pattern.name;
    this.descriptionEl.value = pattern.description || '';
    this.statusSelect.value = pattern.status;

    // Update type badge
    this.typeEl.textContent = pattern.type.toUpperCase();
    this.typeEl.style.background = `${this.typeColors[pattern.type]}20`;
    this.typeEl.style.color = this.typeColors[pattern.type];

    // Update status indicator
    this.updateStatusIndicator();
    this.updateCharCount();

    // Update stats
    this.updateStats(pattern);

    // Render examples
    this.renderExamples(pattern);

    // Load similar patterns for merge suggestions
    await this.loadMergeSuggestions(pattern);

    // Show modal
    this.overlayEl.classList.remove('hidden');
    this.isOpen = true;

    // Focus title
    setTimeout(() => {
      this.titleInput.focus();
      this.titleInput.select();
    }, 100);
  }

  /**
   * Close modal
   */
  close(): void {
    if (this.hasChanges) {
      const confirmed = confirm('You have unsaved changes. Are you sure you want to close?');
      if (!confirmed) return;
    }

    this.overlayEl.classList.add('hidden');
    this.isOpen = false;
    this.pattern = null;
    this.hasChanges = false;
  }

  /**
   * Save pattern changes
   */
  private async save(): Promise<void> {
    if (!this.pattern) return;

    const name = this.titleInput.value.trim();
    if (!name) {
      alert('Pattern name is required');
      this.titleInput.focus();
      return;
    }

    const saveBtn = document.getElementById('pattern-modal-save') as HTMLButtonElement;
    saveBtn.disabled = true;
    saveBtn.innerHTML = '<span class="material-symbols-outlined rotating">sync</span> Saving...';

    try {
      const updated = await patternsApi.updatePattern(this.pattern.type, this.pattern.id, {
        name,
        description: this.descriptionEl.value.trim(),
        status: this.statusSelect.value as PatternStatus
      });

      this.hasChanges = false;

      if (this.onSaveCallback) {
        this.onSaveCallback(updated);
      }

      this.overlayEl.classList.add('hidden');
      this.isOpen = false;
    } catch (error) {
      console.error('Failed to save pattern:', error);
      alert('Failed to save pattern. Please try again.');
    } finally {
      saveBtn.disabled = false;
      saveBtn.innerHTML = '<span class="material-symbols-outlined">save</span> Save Changes';
    }
  }

  /**
   * Confirm and delete pattern
   */
  private async confirmDelete(): Promise<void> {
    if (!this.pattern) return;

    const confirmed = confirm(
      `Are you sure you want to delete "${this.pattern.name}"?\n\n` +
      `This will remove the pattern and all associated data. This action cannot be undone.`
    );

    if (!confirmed) return;

    const deleteBtn = document.getElementById('pattern-modal-delete') as HTMLButtonElement;
    deleteBtn.disabled = true;
    deleteBtn.textContent = 'Deleting...';

    try {
      await patternsApi.deletePattern(this.pattern.type, this.pattern.id);

      if (this.onDeleteCallback) {
        this.onDeleteCallback(this.pattern.id, this.pattern.type);
      }

      this.hasChanges = false;
      this.overlayEl.classList.add('hidden');
      this.isOpen = false;
    } catch (error) {
      console.error('Failed to delete pattern:', error);
      alert('Failed to delete pattern. Please try again.');
    } finally {
      deleteBtn.disabled = false;
      deleteBtn.textContent = 'Delete';
    }
  }

  /**
   * Update character count display
   */
  private updateCharCount(): void {
    const count = this.descriptionEl.value.length;
    this.charCountEl.textContent = `${count}/300`;
    this.charCountEl.classList.toggle('over-limit', count >= 280);
  }

  /**
   * Update status indicator badge
   */
  private updateStatusIndicator(): void {
    const status = this.statusSelect.value as PatternStatus;
    const labels: Record<PatternStatus, string> = {
      active: 'Active',
      emerging: 'Emerging',
      saturated: 'Saturated',
      declining: 'Declining'
    };

    this.statusIndicator.className = `pattern-modal-status-indicator status-${status}`;
    this.statusIndicator.innerHTML = `
      <span class="status-dot"></span>
      ${labels[status]}
    `;
  }

  /**
   * Update statistics display
   */
  private updateStats(pattern: Pattern): void {
    this.statVideos.textContent = pattern.videoCount.toLocaleString();

    const perf = (pattern.avgPerformance * 100).toFixed(1);
    this.statPerf.textContent = `${perf}%`;

    // Mock week change (in real app, would come from API)
    const weekChange = Math.floor(Math.random() * 40) - 10;
    this.statWeek.textContent = `${weekChange >= 0 ? '+' : ''}${weekChange}%`;
    this.statWeek.className = `stat-value ${weekChange >= 0 ? 'stat-positive' : 'stat-negative'}`;

    // Trend based on status
    const trendConfig: Record<PatternStatus, { icon: string; label: string }> = {
      emerging: { icon: 'trending_up', label: 'Rising' },
      active: { icon: 'trending_flat', label: 'Stable' },
      saturated: { icon: 'trending_flat', label: 'Peaked' },
      declining: { icon: 'trending_down', label: 'Falling' }
    };

    const trend = trendConfig[pattern.status];
    this.statTrend.innerHTML = `
      <span class="material-symbols-outlined">${trend.icon}</span>
      <span>${trend.label}</span>
    `;
    this.statTrend.className = `stat-trend trend-${pattern.status}`;
  }

  /**
   * Render example videos/items
   */
  private renderExamples(pattern: Pattern): void {
    // Parse examples from pattern (stored as JSON string)
    let examples: Array<{ thumbnail?: string; title?: string; source?: string }> = [];

    if (pattern.examples) {
      try {
        examples = JSON.parse(pattern.examples);
      } catch {
        examples = [];
      }
    }

    // Keep add button, prepend examples before it
    const addBtn = this.examplesContainer.querySelector('.pattern-modal-add-example');

    // Remove existing example cards
    this.examplesContainer.querySelectorAll('.pattern-modal-example').forEach(el => el.remove());

    // Add example cards
    examples.slice(0, 4).forEach((example, index) => {
      const card = document.createElement('div');
      card.className = 'pattern-modal-example';
      card.innerHTML = `
        <img src="${example.thumbnail || 'https://via.placeholder.com/120x160/1c1c1e/6b7280?text=No+Image'}" alt="${example.title || 'Example'}" />
        <button class="pattern-modal-example-remove" data-index="${index}">
          <span class="material-symbols-outlined">close</span>
        </button>
      `;

      // Insert before add button
      if (addBtn) {
        this.examplesContainer.insertBefore(card, addBtn);
      } else {
        this.examplesContainer.appendChild(card);
      }

      // Remove handler
      card.querySelector('.pattern-modal-example-remove')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeExample(index);
      });
    });

    // Hide add button if 4 examples
    if (addBtn) {
      (addBtn as HTMLElement).style.display = examples.length >= 4 ? 'none' : '';
    }
  }

  /**
   * Add a new example
   */
  private addExample(): void {
    // In real implementation, this would open a video picker
    alert('Video picker coming soon!\n\nThis will allow you to select videos from your library to use as representative examples.');
    this.hasChanges = true;
  }

  /**
   * Remove an example
   */
  private removeExample(index: number): void {
    if (!this.pattern?.examples) return;

    try {
      const examples = JSON.parse(this.pattern.examples);
      examples.splice(index, 1);
      this.pattern.examples = JSON.stringify(examples);
      this.renderExamples(this.pattern);
      this.hasChanges = true;
    } catch {
      console.error('Failed to remove example');
    }
  }

  /**
   * Open examples management view
   */
  private manageExamples(): void {
    alert('Examples management coming soon!\n\nThis will allow you to add, remove, and reorder representative examples for this pattern.');
  }

  /**
   * Load similar patterns for merge suggestions
   */
  private async loadMergeSuggestions(pattern: Pattern): Promise<void> {
    try {
      // Get patterns of same type
      const result = await patternsApi.getByType(pattern.type, undefined, 10);

      // Filter out current pattern and find similar ones (mock similarity)
      const similar = result.patterns
        .filter(p => p.id !== pattern.id)
        .slice(0, 3)
        .map(p => ({
          pattern: p,
          similarity: Math.floor(Math.random() * 30) + 50 // Mock 50-80% similarity
        }));

      this.renderMergeSuggestions(similar);
    } catch (error) {
      console.error('Failed to load merge suggestions:', error);
      this.mergeList.innerHTML = '<p class="pattern-modal-no-merge">No similar patterns found</p>';
    }
  }

  /**
   * Render merge suggestions
   */
  private renderMergeSuggestions(suggestions: Array<{ pattern: Pattern; similarity: number }>): void {
    if (suggestions.length === 0) {
      this.mergeList.innerHTML = '<p class="pattern-modal-no-merge">No similar patterns found</p>';
      return;
    }

    this.mergeList.innerHTML = suggestions.map(({ pattern, similarity }) => `
      <div class="pattern-modal-merge-item" data-id="${pattern.id}" data-type="${pattern.type}">
        <div class="merge-item-info">
          <span class="merge-item-name">${this.escapeHtml(pattern.name)}</span>
          <span class="merge-item-similarity">${similarity}% similar</span>
        </div>
        <button class="merge-item-btn" data-id="${pattern.id}" data-type="${pattern.type}">Merge</button>
      </div>
    `).join('');

    // Add merge handlers
    this.mergeList.querySelectorAll('.merge-item-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.target as HTMLElement).dataset.id!;
        const type = (e.target as HTMLElement).dataset.type as PatternType;
        this.mergePattern(id, type);
      });
    });
  }

  /**
   * Merge with another pattern
   */
  private mergePattern(targetId: string, targetType: PatternType): void {
    // In real implementation, this would call a merge API
    alert(
      `Merge functionality coming soon!\n\n` +
      `This will combine the current pattern with the selected pattern, ` +
      `merging their videos and statistics.`
    );
  }

  /**
   * Set callback for save events
   */
  onSave(callback: (pattern: Pattern) => void): void {
    this.onSaveCallback = callback;
  }

  /**
   * Set callback for delete events
   */
  onDelete(callback: (patternId: string, patternType: PatternType) => void): void {
    this.onDeleteCallback = callback;
  }

  /**
   * Escape HTML
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
